import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    ReactNode,
} from 'react';
import {
    Animated,
    Easing,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
    Platform,
} from 'react-native';
import { COLORS } from '../theme/themeTokens';

/**
 * SnackbarContext
 * ───────────────
 * A single transient toast pinned near the bottom of the screen, with one
 * optional action button (typically "Undo"). Designed to be lightweight —
 * one snackbar visible at a time, replacing whatever was there before.
 *
 * Why custom and not a library: OneApp has no toast/snackbar lib installed,
 * and pulling in a new dep for two short messages would be heavy.
 *
 * Auto-dismiss: 4 seconds, the standard Material default. Long enough for
 * an Undo to be meaningful, short enough to not feel sticky.
 */

const DEFAULT_DURATION_MS = 4000;
const SLIDE_DURATION_MS = 200;

interface ShowOptions {
    message: string;
    actionLabel?: string;
    /** Called when the user taps the action button. The snackbar is dismissed
     *  automatically before this fires. */
    onAction?: () => void;
    /** Override auto-dismiss; defaults to 4s. */
    durationMs?: number;
}

interface SnackbarContextType {
    show: (opts: ShowOptions) => void;
    hide: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [visible, setVisible] = useState(false);
    const [opts, setOpts] = useState<ShowOptions | null>(null);

    const translateY = useRef(new Animated.Value(80)).current;
    const opacity = useRef(new Animated.Value(0)).current;
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = useCallback(() => {
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
    }, []);

    const animateIn = useCallback(() => {
        Animated.parallel([
            Animated.timing(translateY, {
                toValue: 0,
                duration: SLIDE_DURATION_MS,
                useNativeDriver: true,
                easing: Easing.out(Easing.cubic),
            }),
            Animated.timing(opacity, {
                toValue: 1,
                duration: SLIDE_DURATION_MS,
                useNativeDriver: true,
            }),
        ]).start();
    }, [opacity, translateY]);

    const animateOut = useCallback((onDone?: () => void) => {
        Animated.parallel([
            Animated.timing(translateY, {
                toValue: 80,
                duration: SLIDE_DURATION_MS,
                useNativeDriver: true,
                easing: Easing.in(Easing.cubic),
            }),
            Animated.timing(opacity, {
                toValue: 0,
                duration: SLIDE_DURATION_MS,
                useNativeDriver: true,
            }),
        ]).start(() => onDone?.());
    }, [opacity, translateY]);

    const hide = useCallback(() => {
        clearTimer();
        animateOut(() => {
            setVisible(false);
            setOpts(null);
        });
    }, [animateOut, clearTimer]);

    const show = useCallback(
        (next: ShowOptions) => {
            // If something is already visible, replace its content without a
            // jarring re-animate — just reset the timer.
            clearTimer();
            setOpts(next);
            setVisible(true);
            // Run animateIn after state commit so the layout is ready.
            requestAnimationFrame(() => animateIn());

            const duration = next.durationMs ?? DEFAULT_DURATION_MS;
            dismissTimerRef.current = setTimeout(() => hide(), duration);
        },
        [animateIn, clearTimer, hide],
    );

    // Cleanup on unmount.
    useEffect(() => () => clearTimer(), [clearTimer]);

    const handleAction = () => {
        const cb = opts?.onAction;
        hide();
        // Defer slightly so the snackbar starts sliding out before the parent
        // re-renders from the action callback. Otherwise we'd see a flash.
        setTimeout(() => cb?.(), 50);
    };

    return (
        <SnackbarContext.Provider value={{ show, hide }}>
            {children}
            {visible && opts && (
                <Animated.View
                    pointerEvents="box-none"
                    style={[
                        styles.wrapper,
                        { transform: [{ translateY }], opacity },
                    ]}
                >
                    <View style={styles.bar}>
                        <Text style={styles.message} numberOfLines={2}>
                            {opts.message}
                        </Text>
                        {opts.actionLabel && opts.onAction && (
                            <TouchableOpacity
                                onPress={handleAction}
                                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                style={styles.actionBtn}
                                activeOpacity={0.85}
                            >
                                <Text style={styles.actionLabel}>{opts.actionLabel}</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </Animated.View>
            )}
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = (): SnackbarContextType => {
    const ctx = useContext(SnackbarContext);
    if (!ctx) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return ctx;
};

const styles = StyleSheet.create({
    // The wrapper keeps the snackbar pinned just above the tab bar without
    // capturing taps anywhere outside the bar itself.
    wrapper: {
        position: 'absolute',
        left: 16,
        right: 16,
        // Without a `web` key the web fell back to top: 0, under the status bar; there it sits just above the
        // (Android-style, 80 high) tab bar.
        bottom: Platform.select({ ios: 96, android: 80, web: 92 }) as number,
        alignItems: 'center',
        zIndex: 1000,
        elevation: 8,
    },
    // Light "pill"-style snackbar matching the design ref:
    // soft gray container, dark navy message, dark filled action button on the right.
    bar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.softGrey ?? '#E4E6EB',
        borderRadius: 14,
        paddingVertical: 8,
        paddingLeft: 16,
        paddingRight: 8,
        minHeight: 52,
        width: '100%',
        // Soft shadow so it lifts off any background.
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.18,
        shadowRadius: 6,
    },
    message: {
        color: COLORS.primaryNavy ?? '#2A3F5D',
        flex: 1,
        fontSize: 17,
        fontWeight: '500',
        marginRight: 12,
    },
    actionBtn: {
        backgroundColor: COLORS.primaryNavy ?? '#2A3F5D',
        paddingHorizontal: 18,
        paddingVertical: 8,
        borderRadius: 10,
        minHeight: 36,
        alignItems: 'center',
        justifyContent: 'center',
    },
    actionLabel: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '600',
    },
});
